/**
 * TanStack Router configuration (programmatic, hash history so the
 * Vite dev proxy and any static-host deployment work without server
 * rewrites).
 *
 * V3.1: the whole app sits behind `RootGate` — a cookie-JWT auth guard
 * against the CC server (`/api/v1/auth/me`). Unauthenticated visitors
 * see the login screen regardless of route; the trading pages (which
 * hit the unauthenticated internal data-api) are behind the same UI
 * guard. Fleet-management routes live under `/fleet`.
 */
import {
  createHashHistory, createRootRoute, createRoute, createRouter,
  Navigate,
} from "@tanstack/react-router";

import { Shell } from "./components/AppShell";
import { AgentSelectorProvider } from "./hooks/useAgentSelector";
import { useAuth } from "./hooks/useAuth";
import { LivePage } from "./pages/Live";
import { AnalyticsPage } from "./pages/Analytics";
import { AnalyticsDashboardPage } from "./pages/AnalyticsDashboard";
import { AnalyticsAllMarketsPage } from "./pages/AnalyticsAllMarkets";
import { ManualSimulatorPage } from "./pages/ManualSimulator";
import { AutomatedSimulatorPage } from "./pages/AutomatedSimulator";
import { SavedSimulationsPage } from "./pages/SavedSimulations";
import { ConfigPage } from "./pages/Config";
import { PreviousConfigsPage } from "./pages/PreviousConfigs";
import { SettingsPage } from "./pages/Settings";
import { KronosSettingsPage } from "./pages/KronosSettings";
import { StorageSettingsPage } from "./pages/StorageSettings";
import { GrafanaPage } from "./pages/Grafana";
import { GrafanaIndexPage } from "./pages/GrafanaIndex";
import { LoginPage } from "./pages/fleet/Login";
import { AuthSetupPage } from "./pages/fleet/AuthSetup";
import { FleetPage } from "./pages/fleet/Fleet";
import { FleetBotDetailPage } from "./pages/fleet/FleetBotDetail";
import { FleetAssignmentsPage } from "./pages/fleet/FleetAssignments";
import { FleetSignalsPage } from "./pages/fleet/FleetSignals";
import { FleetAuditPage } from "./pages/fleet/FleetAudit";
import { UsersPage } from "./pages/fleet/Users";
import { IncidentsPage } from "./pages/fleet/Incidents";
import { ControlsPage } from "./pages/fleet/Controls";
import { OperationsPage } from "./pages/fleet/Operations";
import { MarketManagementPage } from "./pages/MarketManagement";
import { CollectionHealthPage } from "./pages/CollectionHealth";
import { FleetChannelsPage } from "./pages/fleet/FleetChannels";
import { FleetNotificationsPage } from "./pages/fleet/FleetNotifications";

/**
 * Auth guard wrapping the entire app. While the session probe is in
 * flight nothing is rendered (avoids a login flash); once resolved,
 * either the login screen or the full shell (trading + fleet pages).
 */
function RootGate() {
  const { isAuthenticated, isLoading, step } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <LoginPage />;
  // Restricted first-login mode: password/profile setup and (admins) MFA
  // enrollment must finish before any application route renders.
  if (step && step !== "ready") return <AuthSetupPage step={step} />;
  return (
    <AgentSelectorProvider>
      <Shell />
    </AgentSelectorProvider>
  );
}

const rootRoute = createRootRoute({ component: RootGate });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => <Navigate to="/live" replace />,
});

// Only reachable when authenticated (RootGate short-circuits to the
// login screen otherwise), so simply bounce to the default page.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => <Navigate to="/live" replace />,
});

const liveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/live",
  component: LivePage,
});
const marketDataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/market-data",
  component: () => <Navigate to="/market-data/current" replace />,
});
const marketDataCurrentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/market-data/current",
  component: AnalyticsPage,
});
// Back-compat: /market-data/btc-5m was the V3-prerelease URL for the
// per-market data page; keep it redirecting so saved bookmarks land
// on the new /market-data/current route.
const marketDataBtc5mLegacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/market-data/btc-5m",
  component: () => <Navigate to="/market-data/current" replace />,
});
const windowsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/windows",
  component: () => <Navigate to="/market-data/current" replace />,
});
const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics",
  component: () => <Navigate to="/analytics/current" replace />,
});
const analyticsCurrentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics/current",
  component: AnalyticsDashboardPage,
});
const analyticsAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics/all",
  component: AnalyticsAllMarketsPage,
});
// Back-compat: /analytics/btc-5m was the V3-prerelease URL for the
// per-market summary page; keep it redirecting so any saved
// bookmarks land on the new /analytics/current route.
const analyticsBtc5mLegacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics/btc-5m",
  component: () => <Navigate to="/analytics/current" replace />,
});
const analyticsManualSimulatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics/manual-simulator",
  component: ManualSimulatorPage,
});
const analyticsAutomatedSimulatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics/automated-simulator",
  component: AutomatedSimulatorPage,
});
const analyticsSavedSimulationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics/saved-simulations",
  component: SavedSimulationsPage,
});
const configRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config",
  component: () => <Navigate to="/config/current" replace />,
});
const currentConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/current",
  component: ConfigPage,
});
const previousConfigsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/config/previous",
  component: PreviousConfigsPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: () => <Navigate to="/settings/general" replace />,
});
const settingsGeneralRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/general",
  component: SettingsPage,
});
const settingsKronosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/kronos",
  component: KronosSettingsPage,
});
const settingsStorageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/storage",
  component: StorageSettingsPage,
});
const grafanaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/grafana",
  component: GrafanaIndexPage,
});
const grafanaDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/grafana/$dashboardId",
  component: GrafanaPage,
});

// ─── V3.1 fleet-management routes (ported CC pages) ─────────────────────────
const fleetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet",
  component: FleetPage,
});
const fleetEnrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/enroll",
  component: () => <FleetPage enrollDefaultOpen />,
});
const fleetAssignmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/assignments",
  component: FleetAssignmentsPage,
});
const fleetSignalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/signals",
  component: FleetSignalsPage,
});
const fleetAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/audit",
  component: FleetAuditPage,
});
const fleetChannelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/channels",
  component: FleetChannelsPage,
});
const fleetNotificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/notifications",
  component: FleetNotificationsPage,
});
// Upgrade-plan screens (plan §18): users, incidents, pause controls,
// operations, market management, collection health.
const fleetUsersRoute = createRoute({ getParentRoute: () => rootRoute, path: "/fleet/users", component: UsersPage });
const fleetIncidentsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/fleet/incidents", component: IncidentsPage });
const fleetControlsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/fleet/controls", component: ControlsPage });
const fleetOperationsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/fleet/operations", component: OperationsPage });
const marketManagementRoute = createRoute({ getParentRoute: () => rootRoute, path: "/market-data/manage", component: MarketManagementPage });
const collectionHealthRoute = createRoute({ getParentRoute: () => rootRoute, path: "/market-data/collection", component: CollectionHealthPage });
export const fleetBotDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fleet/$botId",
  component: FleetBotDetailPage,
});

const tree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  liveRoute,
  marketDataRoute,
  marketDataCurrentRoute,
  marketDataBtc5mLegacyRoute,
  windowsRedirectRoute,
  analyticsRoute,
  analyticsCurrentRoute,
  analyticsAllRoute,
  analyticsBtc5mLegacyRoute,
  analyticsManualSimulatorRoute,
  analyticsAutomatedSimulatorRoute,
  analyticsSavedSimulationsRoute,
  configRoute,
  currentConfigRoute,
  previousConfigsRoute,
  settingsRoute,
  settingsGeneralRoute,
  settingsKronosRoute,
  settingsStorageRoute,
  grafanaRoute,
  grafanaDashboardRoute,
  fleetRoute,
  fleetEnrollRoute,
  fleetAssignmentsRoute,
  fleetSignalsRoute,
  fleetAuditRoute,
  fleetChannelsRoute,
  fleetNotificationsRoute,
  fleetUsersRoute,
  fleetIncidentsRoute,
  fleetControlsRoute,
  fleetOperationsRoute,
  marketManagementRoute,
  collectionHealthRoute,
  fleetBotDetailRoute,
]);

export const router = createRouter({
  routeTree: tree,
  history: createHashHistory(),
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register { router: typeof router; }
}
